import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { X, Home, FolderOpen } from 'lucide-react'
import { getFolders, moveDocument } from '../../services/api'
import type { Folder } from '../../types'

interface MoveDocumentModalProps {
  documentIds: string[]
  currentFolderId: string | null
  projectId: string
  onClose: () => void
}

export default function MoveDocumentModal({ documentIds, currentFolderId, projectId, onClose }: MoveDocumentModalProps) {
  const [targetFolderId, setTargetFolderId] = useState<string | null>(currentFolderId)
  const [moveError, setMoveError] = useState('')
  const queryClient = useQueryClient()

  const { data: folders } = useQuery({
    queryKey: ['folders', projectId],
    queryFn: () => getFolders(projectId),
  })

  const moveMutation = useMutation({
    mutationFn: async () => {
      const dest = targetFolderId || 'root'
      for (const docId of documentIds) {
        await moveDocument(docId, dest)
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents'] })
      onClose()
    },
    // BUG-126: without onError the modal sat open with no feedback when a move
    // failed. Surface the real error inline instead of closing silently.
    onError: (err: Error & { response?: { data?: { error?: string } } }) => {
      setMoveError(err.response?.data?.error || err.message || 'Failed to move document(s)')
    },
  })

  // Build indented folder list from hierarchy
  const buildTree = (parentId: string | null, depth: number): { folder: Folder; depth: number }[] => {
    if (!folders) return []
    const children = folders.filter((f) =>
      parentId === null
        ? !f.parent_folder_id || f.parent_folder_id === 'root'
        : f.parent_folder_id === parentId
    )
    const result: { folder: Folder; depth: number }[] = []
    for (const child of children) {
      result.push({ folder: child, depth })
      result.push(...buildTree(child.folder_id, depth + 1))
    }
    return result
  }

  const folderTree = buildTree(null, 0)
  const isSameLocation = targetFolderId === currentFolderId
  const label = documentIds.length === 1 ? 'Move Document' : `Move ${documentIds.length} Documents`

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-[#0c4a6e] rounded-xl border border-white/20 backdrop-blur-md shadow-xl max-w-md w-full mx-4 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white">{label}</h3>
          <button onClick={onClose} className="p-1 text-white/40 hover:text-white/60">
            <X size={18} />
          </button>
        </div>

        <div>
          <label className="block text-sm font-medium text-white/80 mb-2">Destination</label>
          <div className="border border-white/20 rounded-lg max-h-64 overflow-y-auto">
            <button
              onClick={() => setTargetFolderId(null)}
              className={`w-full flex items-center gap-2 px-3 py-2 text-sm ${
                targetFolderId === null ? 'bg-iflow-500/20 text-iflow-300 font-medium' : 'text-white/80 hover:bg-white/5'
              }`}
            >
              <Home size={14} /> Project Root
            </button>
            {folderTree.map(({ folder, depth }) => (
              <button
                key={folder.folder_id}
                onClick={() => setTargetFolderId(folder.folder_id)}
                className={`w-full flex items-center gap-2 px-3 py-2 text-sm ${
                  targetFolderId === folder.folder_id ? 'bg-iflow-500/20 text-iflow-300 font-medium' : 'text-white/80 hover:bg-white/5'
                }`}
                style={{ paddingLeft: `${depth * 16 + 12}px` }}
              >
                <FolderOpen size={14} /> {folder.name}
              </button>
            ))}
          </div>
        </div>

        {moveError && (
          <p data-testid="move-error" className="text-sm text-red-300 mt-4">{moveError}</p>
        )}

        <div className="flex justify-end gap-3 mt-6">
          <button onClick={onClose} className="px-4 py-2 text-sm text-white/80 bg-white/10 border border-white/20 rounded-lg hover:bg-white/20">
            Cancel
          </button>
          <button
            onClick={() => { setMoveError(''); moveMutation.mutate() }}
            disabled={isSameLocation || moveMutation.isPending}
            className="px-4 py-2 text-sm text-white bg-iflow-700 rounded-lg hover:bg-iflow-800 disabled:opacity-50"
          >
            {moveMutation.isPending ? 'Moving...' : 'Move'}
          </button>
        </div>
      </div>
    </div>
  )
}
